// Lib
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Capacitor } from '@capacitor/core';

// Utils
import { getScreenOrientation } from '../utils/platformInitialisationUtils';
import globalLogger from '../../logger/logger';
import { withPayloadLogging } from '../../capacitor/capacitorPayloadLogging';
import virtualKeyboardHeightSingleton from '../../utils/keyboard/virtualKeyboardHeightSingleton';
import { isPlatformAndroid } from '../utils/platformDetailsUtils';

// Selectors
import {
    getMilanoteApplicationModeSelector,
    getPlatformDetailsSelector,
    getPlatformFormFactorSelector,
    selectScreenOrientation,
} from '../platformSelector';
import { selectIsMobileLandscapeModeAllowed } from './mobileScreenOrientationSelector';

// Actions
import { updatePlatformDetails } from '../platformActions';

// Plugins
import { DeviceInformation } from '../../../capacitor_plugins/deviceInformation';
import { KeyboardPlugin } from '../../../capacitor_plugins/keyboard';

// Constants
import {
    CAPACITOR_DEVICE_INFORMATION_PLUGIN,
    CAPACITOR_MN_KEYBOARD_PLUGIN,
} from '../../../capacitor_plugins/pluginConstants';

type ChangeEvent = Event & {
    target: {
        type: OrientationType;
    };
};

const logger = globalLogger.createChannel('screen-orientation-listener');

/**
 * Determines the screen orientation to render the app in.
 */
export default function useScreenOrientationListener() {
    const appMode = useSelector(getMilanoteApplicationModeSelector);
    const formFactor = useSelector(getPlatformFormFactorSelector);
    const currentScreenOrientation = useSelector(selectScreenOrientation);
    const isLandscapeAllowed = useSelector(selectIsMobileLandscapeModeAllowed);
    const platformDetails = useSelector(getPlatformDetailsSelector);

    const dispatch = useDispatch();

    // When the appMode or formFactor changes, recalculate the screen orientation
    useEffect(() => {
        const nextScreenOrientation = getScreenOrientation(formFactor, appMode);

        if (currentScreenOrientation === nextScreenOrientation) return;

        dispatch(updatePlatformDetails({ screenOrientation: nextScreenOrientation }));
    }, [appMode, formFactor]);

    // When the screen orientation changes, update the platform details
    useEffect(() => {
        const abortController = new AbortController();

        screen.orientation?.addEventListener(
            'change',
            (event) => {
                if ((event as ChangeEvent).target.type === currentScreenOrientation) return;

                dispatch(updatePlatformDetails({ screenOrientation: (event as ChangeEvent).target.type }));
            },
            { signal: abortController.signal },
        );

        return () => {
            abortController.abort();
        };
    }, [currentScreenOrientation]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN)) return;

        (async () => {
            try {
                const { keyboardHeight } = await KeyboardPlugin.getKeyboardHeight();
                virtualKeyboardHeightSingleton.resetHeight();
                virtualKeyboardHeightSingleton.setInitialMaxHeight(keyboardHeight);
            } catch (error) {
                logger.error('Failed to reset keyboard height', error);
            }
        })();
    }, [currentScreenOrientation]);

    // Update the native apps when landscape permission is set/changed
    useEffect(() => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_DEVICE_INFORMATION_PLUGIN)) return;
        if (!isPlatformAndroid(platformDetails)) return;

        withPayloadLogging(
            `${CAPACITOR_MN_KEYBOARD_PLUGIN}.setDeviceAttributes`,
            (payload) => DeviceInformation.setDeviceAttributes(payload),
            { isLandscapeAllowed },
        ).catch((error) => {
            logger.error('Failed to set device attributes: ', error);
        });
    }, [isLandscapeAllowed, platformDetails]);
}
